import {DetailScreen} from '../mapping-classes/detail-screen';
import {DetailPanel} from '../mapping-classes/detail-panel';
import {DetailLabel} from '../mapping-classes/detail-label';
import {LazyLoadingData} from '../mapping-classes/lazy-loading-data';

export class Volmacht implements DetailScreen {
  buttons;
  restrictedData: boolean;
  title: string;
  panels: DetailPanel[];
  nameValues: DetailLabel[];
  shieldedText = 'Afgeschermd';
  errorText: string;

  constructor(response: any) {
    this.title = 'Volmacht';
    this.restrictedData = false;
    if (response.detailsViewObject) {
      this.title = response.detailsViewObject.commercialProductTypeName || this.title;
    }

    if (response.listOfError && response.listOfError.length) {
      this.createErrorMessage(response);
    } else {
      this.panels = [];
      this.nameValues = [];
      this.setPanels();
      this.setNameAndValues(response);
      this.addValues(response);
    }
  }

  createErrorMessage(response) {
    if (response.listOfError && response.listOfError.length !== 0) {
      if (response.listOfError[0].code === 'CIV_F025') {
        this.errorText = 'Geen detail gegevens gevonden.';
      } else if (response.listOfError[0].code) {
        this.errorText = 'Product niet gevonden.';
      }
    }
  }

  setPanels() {
    this.panels.push(new DetailPanel('Productkenmerken'));
    this.panels.push(new DetailPanel('Gevolmachtigde rekeningen'));
    this.panels.push(new DetailPanel('Betrokken partijen'));
    this.panels.push(new DetailPanel('Autorisatie'));
    const betrokkenPartijenPanel: DetailPanel = this.panels[2];
    betrokkenPartijenPanel.subPanels.push(new DetailPanel('Volmachtgever'));
    betrokkenPartijenPanel.subPanels.push(new DetailPanel('Gevolmachtigde'));
  }

  setNameAndValues(response?) {
    const obj = response.detailsViewObject;
    const productKenmerken: DetailPanel = this.panels[0];
    const betrokkenPartijen: DetailPanel = this.panels[2];
    const authorisatie: DetailPanel = this.panels[3];

    this.fillProductKenmerkenPanel(productKenmerken);
    this.fillAuthorisatiePanel(authorisatie, obj);

    if (obj.grantors.length === 1 ) {
      this.fillGrantorsPanel(betrokkenPartijen);
    }

    if (obj.grantees.length === 1 ) {
      this.fillGranteesPanel(betrokkenPartijen);
    }
  }

  addValues(response: any) {
    const obj = response.detailsViewObject;
    const productKenmerken: DetailPanel = this.panels[0];
    const gevolmachtigdeRekeningen: DetailPanel = this.panels[1];
    const betrokkenPartijen: DetailPanel = this.panels[2];
    const authorisatie: DetailPanel = this.panels[3];
    this.addValuesToProductKenmerkenPanel(productKenmerken, obj);
    this.addValuesToGevolmachtigdeRekeningenPanel(gevolmachtigdeRekeningen, obj);
    this.addValuesToBetrokkenPartijenPanel(betrokkenPartijen, obj);
    this.addValuesToAuthorisatiePanel(authorisatie, obj);
  }

  private fillProductKenmerkenPanel(productKenmerken: DetailPanel) {
    productKenmerken.nameAndValues.set('powerOfAttorneyArrangementNumber', new DetailLabel('Contractnummer'));
    productKenmerken.nameAndValues.set('creationDate', new DetailLabel('Opvoerdatum'));
  }

  private addValuesToProductKenmerkenPanel(productKenmerken: DetailPanel, obj: any) {
    productKenmerken.nameAndValues.get('powerOfAttorneyArrangementNumber').setValue(obj.powerOfAttorneyArrangementNumber || '-');
    productKenmerken.nameAndValues.get('powerOfAttorneyArrangementNumber').setHasLinkToSameTab(true);
    productKenmerken.nameAndValues.get('powerOfAttorneyArrangementNumber').setIsPowerOfAttorney(true);
    productKenmerken.nameAndValues.get('powerOfAttorneyArrangementNumber').setUrlLink(obj.powerOfAttorneyArrangementUrl);
    productKenmerken.nameAndValues.get('creationDate').setValue(obj.creationDate || '-');
  }

  private addValuesToGevolmachtigdeRekeningenPanel(gevolmachtigdeRekeningen: DetailPanel, obj: any) {
    gevolmachtigdeRekeningen.hasTable = true;
    gevolmachtigdeRekeningen.lazyLoadingData = new LazyLoadingData(null, null, 'volmacht');
    gevolmachtigdeRekeningen.lazyLoadingData.data = obj.products || [];
  }

  private fillGrantorsPanel(betrokkenPartijen: DetailPanel) {
    const grantorSubPanel: DetailPanel = betrokkenPartijen.subPanels[0];
    grantorSubPanel.nameAndValues.set('naamGrantors', new DetailLabel('Naam'));
    grantorSubPanel.nameAndValues.set('typeGrantors', new DetailLabel('Type'));
  }

  private fillGranteesPanel(betrokkenPartijen: DetailPanel) {
    const granteeSubPanel: DetailPanel = betrokkenPartijen.subPanels[1];
    granteeSubPanel.nameAndValues.set('naamGrantees', new DetailLabel('Naam'));
    granteeSubPanel.nameAndValues.set('typeGrantees', new DetailLabel('Type'));
  }

  private addValuesToBetrokkenPartijenPanel(betrokkenPartijen: DetailPanel, obj: any) {
    const grantorSubPanel: DetailPanel = betrokkenPartijen.subPanels[0];
    if (obj.grantors.length > 1) {
      grantorSubPanel.hasTable = true;
      grantorSubPanel.lazyLoadingData = new LazyLoadingData(null, null, 'volmachtGrantors');
      grantorSubPanel.lazyLoadingData.data = obj.grantors || [];
    } else {
      grantorSubPanel.nameAndValues.get('naamGrantors').setValue(obj.grantors[0].name || '-');
      grantorSubPanel.nameAndValues.get('naamGrantors').setHasLinkToSameTab(true);
      grantorSubPanel.nameAndValues.get('naamGrantors').setUrlLink(this.formUrl(obj.grantors[0].involvedPartyCode, obj.grantors[0].siebelId));
      grantorSubPanel.nameAndValues.get('typeGrantors').setValue(obj.grantors[0].involvedPartyCode || '-');
    }

    const granteeSubPanel: DetailPanel = betrokkenPartijen.subPanels[1];
    if (obj.grantees.length > 1) {
      granteeSubPanel.hasTable = true;
      granteeSubPanel.lazyLoadingData = new LazyLoadingData(null, null, 'volmachtGrantees');
      granteeSubPanel.lazyLoadingData.data = obj.grantees || [];
    } else {
      granteeSubPanel.nameAndValues.get('naamGrantees').setValue(obj.grantees[0].name || '-');
      granteeSubPanel.nameAndValues.get('naamGrantees').setHasLinkToSameTab(true);
      granteeSubPanel.nameAndValues.get('naamGrantees').setUrlLink(this.formUrl(obj.grantees[0].involvedPartyCode, obj.grantees[0].siebelId));
      granteeSubPanel.nameAndValues.get('typeGrantees').setValue(obj.grantees[0].involvedPartyCode || '-');
    }
  }

  private fillAuthorisatiePanel(authorisatie: DetailPanel, obj: any) {
    if (obj.otherAuthorizations.length === 0) {
      authorisatie.nameAndValues.set('otherAuthorizations', new DetailLabel('Overige Autorisaties'));
    }

    for (let i = 0; i < obj.otherAuthorizations.length; i++) {
      if (i === 0) {
        authorisatie.nameAndValues.set('otherAuthorizations', new DetailLabel('Overige Autorisaties'));
      }
      if (i > 0 && i < obj.otherAuthorizations.length) {
        authorisatie.nameAndValues.set('otherAuthorizations' + i, new DetailLabel(''));
      }
    }
  }

  private addValuesToAuthorisatiePanel(authorisatie: DetailPanel, obj: any) {
    if (obj.otherAuthorizations === 0) {
      authorisatie.nameAndValues.get('otherAuthorizations').setValue('-');
    }

    for (let i = 0; i < obj.otherAuthorizations.length; i++) {
      if (i === 0) {
        authorisatie.nameAndValues.get('otherAuthorizations').setValue(obj.otherAuthorizations[0]);
      }
      if (i > 0 && i < obj.otherAuthorizations.length) {
        authorisatie.nameAndValues.get('otherAuthorizations' + i).setValue(obj.otherAuthorizations[i]);
      }
    }
  }

  private formUrl(customerType: string, siebelId: string) {
    if(customerType === 'Natuurlijk persoon')
    {
      return 'https://siebel.rabobank.nl/fins_nld/redirectService.htm?ViewName=CustomerHouseholdSummary&MapParam1=' + siebelId;
    }
    else if(customerType === 'Organisatie') {
      return 'https://siebel.rabobank.nl/fins_nld/redirectService.htm?ViewName=CustomerAccountSummary&MapParam1=' + siebelId;
    }
  }
}
